import { runtimeConfig } from './runtime';

/**
 * What this build shows.
 *
 * Several parts of the product are finished enough to work on and not
 * finished enough to put in front of a traveller: the planning assistant, the
 * admin screens that import cities and manage operators, saved trips, and the
 * API documentation. They are all still built and still routed — this only
 * decides whether anything offers them.
 *
 * Set per deployment in /config.js (`mode`), so the one bundle promoted from
 * dev to prod shows the in-progress features in dev and hides them in prod.
 * TP_WEB_MODE at build time is the fallback, which local development uses.
 *
 * The default is the strict one. A deployment that sets neither gets
 * the smaller product rather than accidentally publishing the admin panel,
 * which is the failure worth defaulting away from. Development sets
 * TP_WEB_MODE=dev in .env, as the checked-in .env.example does.
 *
 * This is VISIBILITY, not access control. Every screen behind it is still
 * served and every RPC behind it still answers; what stops the wrong person
 * using them is the login and the permissions on each procedure, exactly as
 * before. Hiding a button has never been a way to secure anything.
 */
const mode = runtimeConfig.mode ?? import.meta.env.TP_WEB_MODE ?? 'production';

/**
 * `?mode=dev` on any URL, for looking at a deployed build.
 *
 * A question that comes up on every environment — "is this deployment showing
 * the in-progress screens?" — otherwise needs a rebuild or a /config.js edit
 * to answer. The param answers it in place, on the machine asking.
 *
 * Safe for the same reason the rest of this file is: it decides what is
 * OFFERED, never what is permitted. Every screen it reveals is already served
 * and every RPC behind one still checks the login and its own permissions, so
 * appending this to a production URL shows an operator the doors, not the way
 * through them.
 *
 * Read once, at module load, so it stays a synchronous constant like `mode`.
 */
const urlMode = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search).get('mode')
  : null;

/** True in a development build; false in anything shipped to travellers. */
export const isDevBuild = [mode, urlMode].some(
  (m) => m === 'dev' || m === 'development',
);

/**
 * Named one per feature rather than testing isDevBuild at each site, so what
 * is hidden reads in one place, and a feature can graduate to production on
 * its own without hunting for its checks.
 */
export const FEATURES = {
  /** The planning assistant, in the planner and its two settings screens. */
  planningAgent: isDevBuild,
  /**
   * Admin, Cities: the city import pipeline.
   *
   * Graduated. Bringing a city in is how a deployment gets anything to plan
   * with at all, and it was only ever behind the dev flag because it shipped
   * while the steps were still being built. An operator who cannot add a
   * city has an empty product and no way to fill it.
   *
   * Still permission-gated on every procedure behind it, as this file's note
   * above says: this decides who is OFFERED the screen, not who may run a
   * build from it.
   */
  cityImport: true,
  /**
   * Admin, Users and Roles.
   *
   * Graduated for the same reason. A deployment with more than one person in
   * it needs someone able to add the second — and with this off, the only
   * account that exists is the one the database was seeded with.
   *
   * Permission-gated as above: being offered the screen is not the same as
   * being allowed to change a role on it.
   */
  userManagement: true,
  /**
   * Admin, Categories: per-category stay durations.
   *
   * Graduated, for the same reason as the travel speeds. A category's stay
   * time is what every place found in OpenStreetMap is scheduled by, so an
   * operator whose museums are allowed ninety minutes and shouldn't be has
   * no other way to say so — and no amount of adjusting a single round fixes
   * it for the next one.
   *
   * Still permission-gated on every procedure behind it: this decides who is
   * OFFERED the screen, not who may change anything on it.
   */
  categoryConfig: true,
  /**
   * Admin, Settings: the blocks that tune the RANKING rather than the round.
   *
   * How many candidates the optimiser gets to choose between, what makes a
   * place score highly, whether an external provider is called for details
   * and photographs, how wide a corridor to search, and the proximity
   * default. Every one of them is a question about how the planner works,
   * asked by somebody building a city — not by an operator running a day, who
   * needs the speeds and the stay times next to them and nothing else.
   */
  plannerTuning: isDevBuild,
  /**
   * Admin, Settings: travel speeds, default avoidances.
   *
   * Graduated. These are the knobs that decide how every round is planned —
   * how fast each mode is assumed to travel, how far the route holds off an
   * excluded place by default. An operator running real rounds needs them,
   * and they were only ever behind the dev flag because they shipped
   * alongside the screens that are still being built.
   *
   * Still permission-gated on every procedure behind it, as this file's note
   * above says: this decides who is OFFERED the screen, not who may use it.
   */
  plannerSettings: true,
  /** Saved trips: the list and the saved-trip view. */
  savedTrips: true,
  /** The planning API documentation. */
  apiDocs: isDevBuild,
  /**
   * The two map overlays that explain the planner to itself: where sites are
   * drawn from, and which ground each city covers.
   *
   * Diagnostics, not planning. They answer "why did this come back empty",
   * which is a question somebody building a city asks and an operator running
   * a round does not.
   *
   * Off everywhere, including in development: they were two switches at the
   * top of the form that nobody planning a round ever wants, sitting between
   * the departure time and the places to visit. The overlays themselves and
   * the code that draws them are untouched, so turning this back to
   * `isDevBuild` restores them.
   */
  mapDiagnostics: false,
  /**
   * The transit step of the city import pipeline.
   *
   * Off: this deployment runs no OpenTripPlanner, so the step could only ever
   * offer an upload button and then explain that there is nothing to build
   * with it — two paragraphs of apology in the middle of a pipeline that
   * otherwise works. The job itself is refused by the server either way.
   *
   * The step and everything behind it are untouched, so a deployment that
   * grows an OTP turns this back on.
   */
  transitImport: false,
} as const;
